import { z } from 'zod'
import { ApiError } from '@/util/errors/api-error'

export enum UserFilesApiErrorCode {
  StaleOperation = 'USER_FILES_STALE_OPERATION',
  NameConflict = 'USER_FILES_NAME_CONFLICT',
}

export const UserFilesApiErrorDetailSchema = z
  .object({
    code: z.nativeEnum(UserFilesApiErrorCode),
    message: z.string(),
    refresh_required: z.boolean().default(false),
    conflicted_file_ids: z.array(z.string()).default([]),
    conflicted_folder_ids: z.array(z.string()).default([]),
  })
  .passthrough()

export type UserFilesApiErrorDetail = z.infer<typeof UserFilesApiErrorDetailSchema>

type ErrorResponseData = {
  detail?: unknown
}

function stringifyDetail(detail: unknown): string {
  if (typeof detail === 'string') return detail
  if (detail == null) return ''

  try {
    return JSON.stringify(detail)
  } catch {
    return String(detail)
  }
}

export function parseUserFilesApiErrorDetail(detail: unknown): UserFilesApiErrorDetail | null {
  const parsed = UserFilesApiErrorDetailSchema.safeParse(detail)
  return parsed.success ? parsed.data : null
}

export function getUserFilesApiErrorDetail(error: unknown): UserFilesApiErrorDetail | null {
  if (!(error instanceof ApiError)) return null
  return parseUserFilesApiErrorDetail(error.detail)
}

export function isExpectedUserFilesApiError(error: unknown): boolean {
  return getUserFilesApiErrorDetail(error) !== null
}

export function isUserFilesStaleOperation(error: unknown): boolean {
  return getUserFilesApiErrorDetail(error)?.code === UserFilesApiErrorCode.StaleOperation
}

export function isUserFilesNameConflict(error: unknown): boolean {
  return getUserFilesApiErrorDetail(error)?.code === UserFilesApiErrorCode.NameConflict
}

export function shouldRefreshAfterUserFilesError(error: unknown): boolean {
  return getUserFilesApiErrorDetail(error)?.refresh_required ?? false
}

export function getUserFilesErrorMessage(error: unknown, fallback: string): string {
  const detail = getUserFilesApiErrorDetail(error)
  if (detail?.message) return detail.message
  if (error instanceof Error && error.message) return error.message
  return fallback
}

export function createUserFilesApiError(params: { status: number; data: unknown; fallbackMessage: string }): ApiError {
  const data = params.data as ErrorResponseData | undefined
  const rawDetail = data?.detail ?? params.data
  const detail = parseUserFilesApiErrorDetail(rawDetail) ?? rawDetail
  const message = parseUserFilesApiErrorDetail(rawDetail)?.message || stringifyDetail(rawDetail) || params.fallbackMessage

  return new ApiError(params.status, message, detail)
}

export function formatUserFilesErrorDetail(data: unknown): string {
  const responseData = data as ErrorResponseData | undefined
  return stringifyDetail(responseData?.detail ?? data)
}
